import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import { parseDeviceId } from '../../utils/deviceUtils';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Avatar,
    Skeleton,
    IconButton,
    Stack,
    Divider,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Build as BuildIcon,
    Schedule as ClockIcon,
    Warning as WarningIcon,
    CheckCircle as SuccessIcon,
    Construction as ToolsIcon,
    Engineering as TechIcon,
    MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { machineLabel } from '../../utils/machineLabel';

const MaintenancePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [maintenanceDevices, setMaintenanceDevices] = useState([]);
    const [upcomingMaintenance, setUpcomingMaintenance] = useState([]);
    const [warnings, setWarnings] = useState([]);

    const fetchMaintenanceData = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams(location.search);
            const typeFilter = params.get('type');

            const [activeRes, upcomingRes, warningsRes] = await Promise.all([
                maintenanceService.getMaintenanceStats(),
                maintenanceService.getUpcomingService({ limit: 100 }),
                maintenanceService.getMachineWarnings(),
            ]);

            let inMaintenance = activeRes.activeDevices || [];
            // Real engine-hours-based service projections from the backend.
            let upcoming = upcomingRes.data || [];
            // Open MACHINE-category alarms surfaced as warnings.
            let machineWarnings = warningsRes.data || [];

            if (typeFilter) {
                const isMatch = (d) => {
                    const info = parseDeviceId(d.device_id || d.equipment_id);
                    return info.type === typeFilter || info.category === typeFilter;
                };
                inMaintenance = inMaintenance.filter(isMatch);
                upcoming = upcoming.filter(isMatch);
                machineWarnings = machineWarnings.filter(isMatch);
            }

            setMaintenanceDevices(inMaintenance);
            setUpcomingMaintenance(upcoming);
            setWarnings(machineWarnings);
        } catch (err) {
            console.error('Failed to fetch maintenance data', err);
        } finally {
            setLoading(false);
        }
    }, [location.search]);

    useEffect(() => {
        fetchMaintenanceData();
    }, [fetchMaintenanceData]);


    return (
        <Box p={3} className="animate-fade-in">
            {/* Header — no page title, the top bar already names the page */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <IconButton onClick={() => navigate('/')} sx={{ bgcolor: 'white', boxShadow: 'var(--shadow-sm)' }}><ArrowBackIcon /></IconButton>
                <Button variant="contained" className="gradient-primary" startIcon={<BuildIcon />}>Initiate Repair</Button>
            </Box>

            {/* Stats Section */}
            <Grid container spacing={3} mb={5}>
                {[
                    { label: 'Active Warnings', value: warnings.length, icon: <WarningIcon />, color: '#ef4444', sub: 'Open machine alarms' },
                    { label: 'Units Under Repair', value: maintenanceDevices.length, icon: <ToolsIcon />, color: '#f59e0b', sub: 'Active work orders' },
                    { label: 'Service Due Soon', value: upcomingMaintenance.filter(d => d.hoursUntil < 20).length, icon: <ClockIcon />, color: '#3b82f6', sub: 'Within 20 engine-hours' },
                    { label: 'Global Availability', value: '98.2%', icon: <SuccessIcon />, color: '#10b981', sub: 'Fleet operational health' }
                ].map((stat, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, borderRadius: '12px' }}>{stat.icon}</Avatar>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{loading ? <Skeleton width={40} /> : stat.value}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>{stat.label}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontSize: '11px', fontWeight: 600 }}>{stat.sub}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={4}>
                {/* Machine Warnings — open MACHINE-category alarms from the health checker */}
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: '24px' }}>
                        <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon sx={{ color: '#ef4444' }} />
                            <Box>
                                <Typography variant="h6" fontWeight={800}>Machine Warnings</Typography>
                                <Typography variant="caption" color="text.secondary">Open threshold &amp; fault alarms requiring attention</Typography>
                            </Box>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}><TableCell sx={{ fontWeight: 700 }}>SERIAL NUMBER</TableCell><TableCell sx={{ fontWeight: 700 }}>WARNING</TableCell><TableCell sx={{ fontWeight: 700 }}>SEVERITY</TableCell><TableCell sx={{ fontWeight: 700 }}>RAISED</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {loading ? [...Array(2)].map((_, i) => <TableRow key={i}><TableCell colSpan={4}><Skeleton height={50} /></TableCell></TableRow>) : warnings.length > 0 ? warnings.map((w, i) => (
                                        <TableRow key={w.id || i} hover>
                                            <TableCell sx={{ fontWeight: 800 }}>{machineLabel(w)}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{w.message || w.alarm_name || 'Machine warning'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={(w.severity || 'warning').toUpperCase()}
                                                    size="small"
                                                    color={w.severity === 'critical' ? 'error' : 'warning'}
                                                    sx={{ fontWeight: 800, borderRadius: '6px', fontSize: '10px' }}
                                                />
                                            </TableCell>
                                            <TableCell><Typography variant="body2" fontWeight={600}>{w.created_at ? new Date(w.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</Typography></TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 6 }}><Box display="flex" flexDirection="column" alignItems="center" gap={1}><SuccessIcon sx={{ color: '#10b981', fontSize: 36 }} /><Typography variant="body2" color="text.secondary">No active machine warnings</Typography></Box></TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card sx={{ borderRadius: '24px' }}>
                        <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" fontWeight={800}>Maintenance Pipeline</Typography>
                            <Typography variant="caption" color="text.secondary">Real-time status of technical interventions</Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}><TableCell sx={{ fontWeight: 700 }}>SERIAL NUMBER</TableCell><TableCell sx={{ fontWeight: 700 }}>INTERVENTION</TableCell><TableCell sx={{ fontWeight: 700 }}>LEAD TECHNICIAN</TableCell><TableCell sx={{ fontWeight: 700 }}>TIMELINE</TableCell><TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {loading ? [...Array(2)].map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton height={50} /></TableCell></TableRow>) : maintenanceDevices.map((device, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell sx={{ fontWeight: 800 }}>{device.device_id}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{device.issue || 'Routine Inspection'}</TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.100', color: 'primary.main', fontSize: '10px', fontWeight: 900 }}>{device.technician?.charAt(0) || '?'}</Avatar>
                                                    <Typography variant="body2" fontWeight={700}>{device.technician || 'Awaiting Assignment'}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{device.start_date} → {device.estimated_completion}</Typography>
                                            </TableCell>
                                            <TableCell><Chip label="IN_PROGRESS" size="small" color="warning" sx={{ fontWeight: 800, borderRadius: '6px', fontSize: '10px' }} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card sx={{ borderRadius: '24px' }}>
                        <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" fontWeight={800}>Service Projections</Typography>
                            <Typography variant="caption" color="text.secondary">Projected service milestones based on engine utilization</Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}><TableCell sx={{ fontWeight: 700 }}>SERIAL NUMBER</TableCell><TableCell sx={{ fontWeight: 700 }}>UTILIZATION</TableCell><TableCell sx={{ fontWeight: 700 }}>NEXT SERVICE</TableCell><TableCell sx={{ fontWeight: 700 }}>PROXIMITY</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>COMMAND</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {loading ? [...Array(2)].map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton height={50} /></TableCell></TableRow>) : upcomingMaintenance.length > 0 ? upcomingMaintenance.map((device, i) => (
                                        <TableRow key={device.device_id || i} hover>
                                            <TableCell sx={{ fontWeight: 800 }}>{machineLabel(device)}</TableCell>
                                            <TableCell><Typography variant="body2" fontWeight={700}>{device.engine_working_hours} h</Typography></TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: device.status === 'OVERDUE' ? 'error.main' : 'text.primary', fontWeight: 800 }}>
                                                    Service {device.checklist} @ {device.nextService} h
                                                </Typography>
                                                {device.interval_desc && <Typography variant="caption" color="text.secondary">{device.interval_desc}</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ width: 300 }}>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <LinearProgress variant="determinate" value={Math.min(100, (device.engine_working_hours / device.nextService) * 100)} sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: 'grey.100' }} />
                                                    <Typography variant="caption" fontWeight={800} color={device.hoursUntil < 20 ? 'error' : 'success'}>
                                                        {device.hoursUntil <= 0 ? 'OVERDUE' : `${device.hoursUntil}h LEFT`}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right"><Button size="small" variant="outlined" sx={{ fontWeight: 800, borderRadius: '8px' }}>Log Schedule</Button></TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}><Typography variant="body2" color="text.secondary">No service projections available — awaiting engine-hour telemetry</Typography></TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box p={2} bgcolor="grey.50" textAlign="center"><Typography variant="caption" color="text.secondary" fontWeight={700}>SYSTEM ESTIMATE BASED ON HISTORICAL ENGINE RUNTIME ANALYSIS</Typography></Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MaintenancePage;
